import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { phoneNumbersApi } from "../../phone-numbers/lib/api";
import type { PhoneNumber } from "../../phone-numbers/lib/types";
import { useCrmContact } from "../api/crm";

export const NO_CALLABLE_NUMBERS_REASON =
  "None of your numbers can place calls yet. Imported numbers need to finish setting up for outbound calls.";

export const canCallFrom = (n: PhoneNumber): boolean =>
  !n.is_imported || n.outbound_caller_id_status === "verified";

// CRM-811: precedence for the "call from" property scope — a specific lead wins
// over the contact-level union. Extracted to avoid a nested ternary at the call site.
const buildListScope = (
  leadId?: number,
  contactId?: string
): { leadId?: number; contactId?: string } | undefined => {
  if (leadId) return { leadId };
  if (contactId) return { contactId };
  return undefined;
};

/**
 * Shared "who to call from / to" selection for the voice composer and the
 * make-a-call modal: the partner's active voice-capable numbers (from) and the
 * contact's phones (to). Seeds sensible defaults (first of each) once loaded.
 */
export const useCallSelection = (
  contactId: string,
  enabled = true,
  // Overrides for callers that have the destination phones/name already (e.g. a
  // lead that isn't backed by a CRM contact) — avoids requiring a contactId.
  toPhonesOverride?: string[],
  toNameOverride?: string,
  // CRM-811 property scoping: when a specific lead is in context (lead detail /
  // pre-dial from a lead) the "call from" list is scoped to that lead's property;
  // otherwise it's scoped to the union of the contact's leads' properties. The
  // server also fail-closes to the user's accessible properties.
  leadId?: number
) => {
  const destinationSupplied = Boolean(toPhonesOverride && toNameOverride);
  const { data: contact } = useCrmContact(destinationSupplied ? "" : contactId);

  const listScope = buildListScope(leadId, contactId);

  const { data: numbersData, isLoading: numbersLoading } = useQuery({
    queryKey: ["VOICEABLE_PHONE_NUMBERS", listScope ?? null],
    queryFn: () => phoneNumbersApi.listAll({ scope: listScope }),
    enabled
  });

  const fromOptions = useMemo(
    () =>
      (numbersData?.items ?? []).filter(
        (n) => n.can_voice && n.status === "active"
      ),
    [numbersData]
  );
  const callableFromOptions = useMemo(
    () => fromOptions.filter(canCallFrom),
    [fromOptions]
  );
  const noCallableNumbers =
    !numbersLoading &&
    fromOptions.length > 0 &&
    callableFromOptions.length === 0;
  const toOptions = useMemo(
    () => toPhonesOverride ?? contact?.phones ?? [],
    [toPhonesOverride, contact]
  );

  const [fromId, setFromId] = useState<number | undefined>(undefined);
  const [toNumber, setToNumber] = useState<string | undefined>(undefined);

  // Seed defaults (first voiceable number, first phone) once data is in. Keep
  // the user's selection only while it's still valid for the current options —
  // otherwise reset, so a reused composer doesn't carry the previous contact's
  // number after a contact switch.
  useEffect(() => {
    if (!enabled) return;
    setFromId((prev) =>
      prev != null && callableFromOptions.some((n) => n.id === prev)
        ? prev
        : callableFromOptions[0]?.id
    );
    setToNumber((prev) =>
      prev && toOptions.includes(prev) ? prev : toOptions[0]
    );
  }, [enabled, callableFromOptions, toOptions]);

  const contactName =
    toNameOverride ??
    (contact
      ? [contact.first_name, contact.last_name]
          .filter((part): part is string => !!part && part.trim().length > 0)
          .join(" ")
          .trim()
      : "");
  const fromLabel = fromOptions.find((n) => n.id === fromId)?.name ?? undefined;

  return {
    fromOptions,
    callableFromOptions,
    noCallableNumbers,
    toOptions,
    fromId,
    setFromId,
    toNumber,
    setToNumber,
    numbersLoading,
    contactName,
    fromLabel
  };
};

export type CallSelection = ReturnType<typeof useCallSelection>;
